import React from 'react'
import type {
  AdBreak,
  AdTagConfig,
  AudioChangedEvent,
  GoogleCastRemoteControlConfig,
  PlayerAPI,
  PlayerConfig,
  PlayerEventBase,
  SeekEvent,
  SubtitleEvent,
  TimeChangedEvent,
  UserInteractionEvent,
  ViewModeChangedEvent,
} from 'bitmovin-player'
import classNames from 'classnames'
import { getCookie } from 'cookies-next'
import { withRouter } from 'next/router'
import { AD_TESTING_COOKIE } from '@/constants/cookies'
import { withAngelPlayerAPI } from '@/organisms/AngelPlayer/AngelPlayerContext'
import { BitmovinUILoader } from '@/organisms/AngelPlayer/BitmovinPlayer'
import { BITMOVIN_UI_PLAYER_VERSION } from '@/organisms/AngelPlayer/BitmovinPlayer/constants'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { logger } from '@/utils/logging'
import { AssetDescription, SmtpeController } from './SMPTEController'
import { AdTagType, Asset, BitmovinPlayerClassProps, PlayerEvent } from './types'

const BASE_REMOTE_CONTROL_CONFIG: GoogleCastRemoteControlConfig = {
  type: 'googlecast',
  receiverApplicationId: process.env.NEXT_PUBLIC_GOOGLE_RECEIVER_APP_ID as string,
  receiverVersion: 'v3',
}

const BASE_PLAYER_CONFIG: PlayerConfig = {
  key: process.env.NEXT_PUBLIC_BITMOVIN_PLAYER_KEY as string,
  cast: {
    enable: true,
  },
  ui: false,
  remotecontrol: BASE_REMOTE_CONTROL_CONFIG,
  playback: {
    autoplay: false,
    muted: false,
  },
}

export class BitmovinPlayerClass extends React.PureComponent<BitmovinPlayerClassProps> {
  private playerRef: React.RefObject<HTMLDivElement>
  private mountedRef: React.MutableRefObject<boolean | null>
  private _player: PlayerAPI | undefined
  private playerInitTime: number | undefined

  constructor(props: BitmovinPlayerClassProps) {
    super(props)
    this.playerRef = React.createRef<HTMLDivElement>()
    this.mountedRef = React.createRef<boolean | null>()
  }

  render() {
    const {
      aspectClassName = 'w-full h-full',
      overlays,
      disableCommunityView,
      showContentShareButton,
      showParentalControlsButton,
      parentalControlsEnabled,
      isLoginRequired,
    } = this.props

    if (isLoginRequired) {
      return (
        <div className="aspect-h-9 aspect-w-16 relative bg-black lg:aspect-h-unset lg:aspect-w-unset lg:h-[60vh]">
          <div className="w-full bg-black" />
        </div>
      )
    }

    return (
      <>
        {this.props.isSourceLoaded && (
          <BitmovinUILoader
            disableCommunityView={disableCommunityView}
            showContentShareButton={showContentShareButton}
            showParentalControlsButton={showParentalControlsButton}
            parentalControlsEnabled={parentalControlsEnabled}
            markers={this.props.markers}
            player={this._player}
            uiLanguage={this.props.uiLanguage}
          />
        )}
        <div
          data-testid="bitmovin-player"
          id="bitmovin-player"
          ref={this.playerRef}
          className={classNames([aspectClassName, 'relative'])}
        >
          {overlays?.map(({ key, Component }) => (
            <Component key={key} />
          ))}
        </div>
      </>
    )
  }

  async componentDidMount() {
    // React 18 mounts every component twice in strict mode in development.  This is a workaround to use a ref to only load the player once in dev.
    if (isDevelopmentEnvironment() && !this.mountedRef.current) {
      this.mountedRef.current = true
      return undefined
    } else {
      this.playerInitTime = Date.now()

      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const bitmovinPlayer = require('bitmovin-player')
      const { Player } = bitmovinPlayer
      const player = new Player(this.playerRef.current as HTMLDivElement, this.buildPlayerConfig())

      try {
        player.on(PlayerEvent.Paused, this._onPaused)
        player.on(PlayerEvent.Playing, this._onPlaying)
        player.on(PlayerEvent.Seek, this._onSeek)
        player.on(PlayerEvent.Seeked, this._onSeeked)
        player.on(PlayerEvent.SourceLoaded, this._onSourceLoaded)
        player.on(PlayerEvent.PlaybackFinished, this._onPlaybackFinished)
        player.on(PlayerEvent.TimeChanged, this._onTimeChanged)
        player.on(PlayerEvent.AudioChanged, this._onAudioChanged)
        player.on(PlayerEvent.SubtitleEnabled, this._onSubtitleEnabled)
        player.on(PlayerEvent.SubtitleDisabled, this._onSubtitleDisabled)
        player.on(PlayerEvent.Ready, this._onReady)
        player.on(PlayerEvent.ViewModeChanged, this._onViewModeChanged)
        player.on(PlayerEvent.AdStarted, this._onAdStarted)
        player.on(PlayerEvent.AdFinished, this._onAdFinished)
        player.on(PlayerEvent.AdSkipped, this._onAdSkipped)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        logger().error(`BitmovinPlayer - Error adding events from player: ${error?.message}`, { player, error })
      }

      this._player = player

      const controller = new SmtpeController(
        player,
        new AssetDescription(this.props.asset),
        player,
        this.props.autoplay,
      )

      this.props.setController(controller)

      this.props.router.events.on('routeChangeStart', this._saveWatchPosition)

      window.addEventListener('beforeunload', this._saveWatchPosition)

      this.loadBitmovinMux()
    }
  }

  buildPlayerConfig(): PlayerConfig {
    const config = { ...BASE_PLAYER_CONFIG }

    if (this.props.enableAds && this.props.asset.vmapUrl) {
      let adUrl = this.props.asset.vmapUrl
      if (shouldForceAds()) adUrl += '&test=true&force_pre_roll=true'

      const adBreak: AdTagConfig = {
        tag: {
          type: AdTagType.VMAP,
          url: adUrl,
        },
      }
      config.advertising = {
        adBreaks: [adBreak],
        strategy: {
          shouldPlayAdBreak: () => true,
          shouldPlaySkippedAdBreaks: (skippedAds: AdBreak[]) => {
            const lastAd = skippedAds[skippedAds.length - 1]
            return lastAd ? [lastAd] : []
          },
        },
        videoLoadTimeout: 10000,
      }
    }

    if (config.playback && this.props.isMuted) {
      config.playback.muted = true
    }

    return config
  }

  loadBitmovinMux() {
    // Script needs to be added *after* the bitmovin player has been created.
    const bitmovinMuxScript = document.createElement('script')
    bitmovinMuxScript.async = true
    bitmovinMuxScript.src = 'https://src.litix.io/bitmovin/5/bitmovin-mux.js'
    this.playerRef.current?.appendChild(bitmovinMuxScript)

    const initWhenLoaded = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (window as any).initBitmovinMux === 'function') {
        this.initBitmovinMux()
      } else {
        setTimeout(initWhenLoaded, 100)
      }
    }
    initWhenLoaded()
  }

  initBitmovinMux() {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const bitmovinPlayer = require('bitmovin-player')
    const muxConfig = {
      debug: false,
      // See https://docs.mux.com/guides/data/make-your-data-actionable-with-metadata for all data options
      data: {
        env_key: process.env.NEXT_PUBLIC_MUX_KEY,

        // Player Metadata
        player_name: 'angel-web',
        player_version: BITMOVIN_UI_PLAYER_VERSION,
        player_init_time: this.playerInitTime,
        player_autoplay: this.props.autoplay,
        player_autoplay_on: this.props.autoplay, // This appears to be an undocumented property that works, while the documented player_autoplay doesn't appear to do anything...

        // User Metadata
        viewer_user_id: this.props.anonymousUserId,
        custom_1: this.props.loggedInUserId || undefined,

        // Video Metadata
        ...this.getMuxVideoMetadata(this.props.asset),
      },
    }

    // NOTE: the third argument is undocumented, but necessary when using the bitmovin
    // npm module instead of loading bitmovin via a script tag.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).initBitmovinMux(this._player, muxConfig, { player: bitmovinPlayer })
    logger().debug('mux initialized', { muxConfig, bitmovinPlayer })
  }

  componentWillUnmount() {
    this.props.controller?.destroy()
    this.props.uiManager?.release()
    this.props.setController(null)
    this.props.setUIManager(null)
    this.props.setIsSourceLoaded(false)
    this.props.router.events.off('routeChangeStart', this._saveWatchPosition)
    window.removeEventListener('beforeunload', this._saveWatchPosition)
  }

  componentDidUpdate(prevProps: Readonly<BitmovinPlayerClassProps>) {
    if (prevProps.asset !== this.props.asset) {
      this.props.controller?.load(new AssetDescription(this.props.asset))

      // Note: `mux` property should be added to the player instance by the initBitmovinMux function
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(this._player as any)?.mux?.emit('videochange', this.getMuxVideoMetadata(this.props.asset))
    }
  }

  getMuxVideoMetadata(asset: Asset) {
    return {
      video_id: asset.analytics.content_id,
      video_series: asset.projectSlug,
      video_title: asset.sourceConfig.subtitle,
      video_content_type: asset.analytics.videoType,
      video_duration: asset.analytics.total_length ? asset.analytics.total_length * 1000 : undefined,
    }
  }

  _onAudioChanged = (event: PlayerEventBase) => this.props.onAudioChanged(event as AudioChangedEvent)

  _onPaused = (event: UserInteractionEvent) => {
    // This forces the player to keep playing when an attempt to pause the ad occurs, like when click the ad video.
    // The Google IMA video player is the one firing this event, or some Bitmovin layer around it,
    // and we don't have a way to prevent it from firing.
    if (event.issuer === 'advertising-api') {
      this.props.controller.play()
    } else {
      this.props.onPaused(event)
    }
  }

  _onPlaybackFinished = () => this.props.onPlaybackFinished()

  _onPlaying = () => this.props.onPlaying()

  _onReady = () => this.props.onReady()

  _saveWatchPosition = () => {
    this.props.saveWatchPosition()
  }

  _onSeek = () => this.props.onSeek()

  _onSkip = () => this.props.onSkip()

  _onSeeked = (event: PlayerEventBase) => this.props.onSeeked(event as SeekEvent)
  _onSourceLoaded = (event: PlayerEventBase) => this.props.onSourceLoaded(event)

  _onSubtitleEnabled = (event: PlayerEventBase) => this.props.onSubtitleEnabled(event as SubtitleEvent)
  _onSubtitleDisabled = (event: PlayerEventBase) => this.props.onSubtitleDisabled(event as SubtitleEvent)

  _onTimeChanged = (event: PlayerEventBase) => this.props?.onTimeChanged?.(event as TimeChangedEvent)

  _onViewModeChanged = (event: PlayerEventBase) => this.props?.onViewModeChanged?.(event as ViewModeChangedEvent)

  _onAdStarted = () => this.props?.onAdStarted?.()
  _onAdFinished = () => this.props?.onAdFinished?.()
  _onAdSkipped = () => this.props?.onAdSkipped?.()
}

function shouldForceAds() {
  const cookie = getCookie(AD_TESTING_COOKIE)
  return cookie === 'force'
}

export const BitmovinPlayer = withRouter(withAngelPlayerAPI<BitmovinPlayerClassProps>(BitmovinPlayerClass))
